import { type ModelChoice, ModelChoice as ModelChoiceSchema } from "@curriculum-studio/core";
import { Either, Schema } from "effect";
import { useCallback, useState } from "react";

// CON-3203 (ADR-0154): the Expert's model/effort pick for studio's generative work, remembered per
// browser. Every generative request reads it at send time — a chat turn, an exercise-authoring run
// and a curriculum generation — so the pick needs no threading through props.
const MODEL_CHOICE_KEY = "curriculum-studio:model-choice";

// `localStorage` is a system boundary that can be absent (jsdom, SSR) or throw on access (sandboxed
// iframes, some private modes) — same feature detection as `review-sidebar.tsx`'s rail width.
const storage = (): Storage | undefined => {
  try {
    const store = window.localStorage;
    return typeof store?.getItem === "function" ? store : undefined;
  } catch {
    return undefined;
  }
};

const decodeChoice = Schema.decodeUnknownEither(Schema.parseJson(ModelChoiceSchema));

// The stored pick, or the empty pick (every surface's own defaults) when nothing valid is stored —
// a hand-edited or retired model id decodes to nothing rather than reaching a request.
export const readModelChoice = (): ModelChoice => {
  const raw = storage()?.getItem(MODEL_CHOICE_KEY);
  if (raw === undefined || raw === null) return {};
  const decoded = decodeChoice(raw);
  return Either.isRight(decoded) ? decoded.right : {};
};

export const writeModelChoice = (choice: ModelChoice): void => {
  try {
    storage()?.setItem(MODEL_CHOICE_KEY, JSON.stringify(choice));
  } catch {
    // A full or blocked store only loses the remembered pick, never the request.
  }
};

// The pick as request-body fields: present only when set, so an unset pick sends nothing and every
// server keeps its own default.
export const modelChoiceFields = (choice: ModelChoice = readModelChoice()): ModelChoice => ({
  ...(choice.model !== undefined ? { model: choice.model } : {}),
  ...(choice.effort !== undefined ? { effort: choice.effort } : {}),
});

export const useModelChoice = (): readonly [ModelChoice, (next: ModelChoice) => void] => {
  const [choice, setChoice] = useState<ModelChoice>(readModelChoice);
  const update = useCallback((next: ModelChoice) => {
    writeModelChoice(next);
    setChoice(next);
  }, []);
  return [choice, update];
};
